import { useCallback, useRef } from "react";
import { Share2 } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { useSelectedNation } from "@/hooks/useSelectedNation";
import { shareNation } from "@/lib/whatsappViral/whatsappEngine";
import { toast } from "sonner";

export type VictoryCardProps = {
  headline: string;
  subline?: string;
  emoji?: string;
  accent?: "rose" | "amber" | "cyan" | "violet";
};

const ACCENT: Record<NonNullable<VictoryCardProps["accent"]>, string> = {
  rose: "oklch(0.65 0.27 27)",
  amber: "oklch(0.82 0.18 75)",
  cyan: "oklch(0.75 0.18 210)",
  violet: "oklch(0.65 0.22 295)",
};

export function VictoryCard({ headline, subline, emoji = "🏆", accent = "rose" }: VictoryCardProps) {
  const { t, lang } = useLanguage();
  const ref = useRef<HTMLDivElement>(null);
  const color = ACCENT[accent];
  // P0-SHARE-02 — share the user's chosen nation, not the geo IP nation.
  const { nation } = useSelectedNation();

  const share = useCallback(async () => {
    const res = await shareNation(
      { nationCode: nation ?? "" },
      { source: "victory_card", lang },
    );
    if (!res.ok) toast.error(t("cm_copy_failed") || "Error");
  }, [nation, lang, t]);

  return (
    <div className="space-y-2">
      <div
        ref={ref}
        className="aspect-[4/5] w-full max-w-xs overflow-hidden rounded-3xl border p-5 text-foreground"
        style={{
          borderColor: color,
          background: `radial-gradient(circle at 30% 20%, ${color}55, transparent 60%), linear-gradient(160deg, oklch(0.18 0.05 280), oklch(0.1 0.04 270))`,
          boxShadow: `0 20px 60px ${color}44`,
        }}
      >
        <div className="text-[10px] uppercase tracking-[0.3em] opacity-70">VerseSport · Mundial 2026</div>
        <div className="mt-6 text-7xl">{emoji}</div>
        <div className="mt-4 text-2xl font-black leading-tight">{headline}</div>
        {subline && <div className="mt-2 text-sm opacity-80">{subline}</div>}
        <div className="mt-auto pt-8 text-[10px] uppercase tracking-widest opacity-60">versesport.com</div>
      </div>
      <button
        type="button"
        onClick={share}
        className="flex w-full max-w-xs items-center justify-center gap-2 rounded-2xl border border-primary/40 bg-primary/10 px-4 py-2.5 text-sm font-black uppercase tracking-wider"
      >
        <Share2 className="h-4 w-4" />
        {t("victory_card_share")}
      </button>
    </div>
  );
}